import { faCheck, faDownload, faEdit, faTimes, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, FormControl, FormLabel, InputGroup, Row } from "react-bootstrap";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { CellProps, Column } from "react-table";
import { ISubscriberImport } from "../../../models/Interfaces";
import FilteredTable from "../../components/common/FilteredTable";
import { emailAddress } from "@form-validation/validator-email-address";
import { notEmpty } from "@form-validation/validator-not-empty";
import { SubscriberService } from "../../../services/SubscriberService";
import Loading from "../../components/common/Loading";
import { Link } from "react-router-dom";

const ImportSubscribers = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [subscribers, setSubscribers] = useState<Array<ISubscriberImport>>([]);
  const [firstRowHeader, setFirstRowHeader] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const subSvcRef = useRef<SubscriberService | null>(null);

  const columns: Column<ISubscriberImport>[] = useMemo(() => [
    {
      Header: "Email Address",
      accessor: "EmailAddress"
    },
    {
      Header: "Firstname",
      accessor: "Firstname"
    },
    {
      Header: "Lastname",
      accessor: "Lastname"
    },
    {
      Header: "Address1",
      accessor: "Address1"
    },
    {
      Header: "Address2",
      accessor: "Address2"
    },
    {
      Header: "Address3",
      accessor: "Address3"
    },
    {
      Header: "Town/City",
      accessor: "City"
    },
    {
      Header: "County",
      accessor: "County"
    },
    {
      Header: "Postcode",
      accessor: "Postcode"
    },
    {
      Header: "Valid",
      Cell: ({ row }: CellProps<ISubscriberImport>) => {
        const valid = validateRow(row.original);
        row.original.Valid = valid;
        return valid ? (
          <FontAwesomeIcon icon={faCheck} size="2x" className="text-success" />
        ) : (
          <FontAwesomeIcon icon={faTimes} size="2x" className="text-danger" />
        );
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<ISubscriberImport>) => (
        <div>
          <Button variant="outline-danger" onClick={() => removeSubscriber(row.original)} className="btn-nomin" title="Delete subscriber">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [subscribers]);

  const selectFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files;

    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const clearSelectedFile = (): void => {
    setSelectedFile(null);
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event) {
      setFirstRowHeader(event.target.checked);
    }
  };

  const removeSubscriber = (sub: ISubscriberImport): void => {

  };

  const saveSubscribers = async (): Promise<void> => {
    if (subscribers.length > 0 && subSvcRef.current) {
      const valid = subscribers.filter(s => s.Valid);

      if (valid.length > 0) {
        setUploading(true);

        const res = await subSvcRef.current.uploadValidSubscribers(subscribers);

        if (res) {
          setSubscribers([]);
        }

        setUploading(false);
      }
    }
  };

  const validateRow = (row: ISubscriberImport): boolean => {
    if (row) {
      // Create an array of validation checks
      const validations = [
        emailAddress().validate({ value: row.EmailAddress }).valid,
        notEmpty().validate({ value: row.EmailAddress }).valid,
        notEmpty().validate({ value: row.Firstname }).valid,
        notEmpty().validate({ value: row.Lastname }).valid,
      ];

      // Use every() to ensure all validations are true
      return validations.every((isValid) => isValid);
    }
    return false;
  };

  const processFile = (): void => {
    if (selectedFile) {
      const reader = new FileReader();

      reader.onload = () => {
        let data;

        // Handle ArrayBuffer if necessary
        if (reader.result instanceof ArrayBuffer) {
          const decoder = new TextDecoder("utf-8");
          data = decoder.decode(reader.result);
        } else {
          data = reader.result;
        }

        if (data) {
          const rows = data.split(/\r\n|\n/);
          if (rows && rows.length > 0) {
            const mappedSubscribers = rows.map((row, index) => {
              if (index === 0 && firstRowHeader) return null;

              const fields = row.split(',');

              if (fields && fields.length === 9) {
                const subscriber = {
                  SiteId: "", //SiteId,
                  EmailAddress: fields[0].replace(/"/g, ''),
                  Firstname: fields[1].replace(/"/g, ''),
                  Lastname: fields[2].replace(/"/g, ''),
                  Address1: fields[3].replace(/"/g, ''),
                  Address2: fields[4].replace(/"/g, ''),
                  Address3: fields[5].replace(/"/g, ''),
                  City: fields[6].replace(/"/g, ''),
                  County: fields[7].replace(/"/g, ''),
                  Postcode: fields[8].replace(/"/g, ''),
                  Valid: false,
                };
                return subscriber;
              }
              return null;
            }).filter(Boolean);

            if (mappedSubscribers.length > 0) {
              setSubscribers(mappedSubscribers as Array<ISubscriberImport>);
            }
          }
        }
      };

      // Read as text to simplify handling if format is expected to be text-based (e.g., CSV)
      reader.readAsText(selectedFile);
    }
  };

  const downloadTemplate = (): void => {
    const mimetype = "application/octet-stream";
    const filename = "subscriber-template.csv";
    let csvcontent = `"email","firstname","lastname","address1","address2","address3","city","county","postcode"\n`;
    csvcontent += `"fred@slaterockgravel.com","Fred","Flintsone","345 Cave Stone Road","Cobblestone","Gavelville","Bedrock","Arkanstone","AR1 0GR"\n`;

    // Type assertion for msSaveBlob support
    const navigatorWithMsSaveBlob = navigator as Navigator & { msSaveBlob?: (blob: Blob, filename: string) => void };

    if (navigatorWithMsSaveBlob.msSaveBlob) {
      // For IE11 and older Edge compatibility
      navigatorWithMsSaveBlob.msSaveBlob(new Blob([csvcontent], { type: mimetype }), filename);
    } else if (URL && typeof URL.createObjectURL === "function") {
      // For modern browsers
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csvcontent], { type: mimetype }));
      a.setAttribute("download", filename);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // Fallback for older browsers
      window.location.href = "data:application/octet-stream," + encodeURIComponent(csvcontent);
    }
  };

  useEffect(() => {
    if (selectedFile) {
      processFile();
    } else {
      setSubscribers([]);
    }
  }, [selectedFile, firstRowHeader]);

  useEffect(() => {
    if (!subSvcRef.current) {
      subSvcRef.current = new SubscriberService();
    }
  }, []);

  return (
    <>
      <div className="panel__header white bold">
        <span>Import Subscribers</span>
      </div>

      {!uploading && (
        <>
          <div className="bg-light">
            <Form id="import-subscribers" className="p-3">
              <Row>
                <Col md={12} lg={3} className="mt-3">
                  <InputGroup>
                    <FormControl
                      type="file"
                      multiple={false}
                      title="select import CSV file"
                      onChange={selectFile}
                      placeholder="select import CSV file"
                    />
                    <InputGroupText className="bg-danger" style={{ cursor: "pointer" }} title="clear file" onClick={clearSelectedFile}>
                      <FontAwesomeIcon icon={faTimes} className="text-white" />
                    </InputGroupText>
                  </InputGroup>
                  <div className="m-1 text-center">
                    <Link to="" onClick={downloadTemplate}>Download CSV Template</Link>
                  </div>
                </Col>
                <Col md={12} lg={2} className="mt-3">
                  <InputGroup className="d-flex align-items-center">
                    <FormCheckInput
                      className="form-check"
                      name="headerRow"
                      id="headerRow"
                      checked={firstRowHeader}
                      onChange={handleCheckChange}
                    />
                    <FormLabel htmlFor="headerRow" className="m-0 p-0 m-2 d-flex align-items-center">First row contains header</FormLabel>
                  </InputGroup>
                </Col>
              </Row>
            </Form>
          </div>
          <div id="subscribers">
            <FilteredTable<ISubscriberImport>
              columns={columns}
              data={subscribers}
              noDataMessage="Select a file to import, download and use the template if required"
              tableBorderClass="border"
              columnHeaderClass="bg-info text-black"
              containerClass="ps-3 pe-3"
              showAddButton={true}
              addButtonText="Upload valid data"
              addButtonIcon={faUpload}
              addButtonHandler={saveSubscribers}
              cellClass="normal align-middle"
            />
          </div>
        </>
      )}

      {uploading && (
        <Loading Message="Uploading imported subscriber details..." SpinSize="5x" />
      )}
    </>
  );
}

export default ImportSubscribers;